import { DeleteOutline } from "@material-ui/icons";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { deleteProduct, getProducts } from "../../redux/apiCalls";
import { publicRequest } from "../../requestMethods";
import excel from "../../components/excel/excel";
import Select from "react-select";
import { commonSelectProps } from "../../components/shared/SelectStyles";
import { FiPlus } from "react-icons/fi";

export default function ProductList() {
    const dispatch = useDispatch();
    const superadmin = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.isSuperAdmin === true : null
    );

    const marketing = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "marketing"
            : null
    );
    const products = useSelector((state) => state.product.products);
    const LIMIT = 30;
    const [inputs, setInputs] = useState({ page: 1, limit: LIMIT });
    const [filterCategory, setFilterCategory] = useState({
        status: undefined,
        category: undefined,
        principle: undefined,
        itemCode: undefined,
    });
    const [categories, setCategories] = useState([]);
    const [principles, setPrinciples] = useState([]);
    const [itemCode, setItemCode] = useState("");

    // Fetch categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await publicRequest.get("/category/all");
                setCategories(res.data);
            } catch (err) {
                console.error("Error fetching categories:", err);
            }
        };
        fetchCategories();
    }, []);

    // Fetch principles (outright users)
    useEffect(() => {
        const fetchPrinciples = async () => {
            try {
                const params = { type: ["outright"] };
                const res = await publicRequest.get("/users/admin", { params });
                setPrinciples(res.data);
            } catch (err) {
                console.error("Error fetching principles:", err);
            }
        };
        fetchPrinciples();
    }, []);

    // Fetch products when filters change
    useEffect(() => {
        getProducts(dispatch, filterCategory);
    }, [dispatch, filterCategory]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setInputs((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
    }, [filterCategory.category, filterCategory.principle, filterCategory.itemCode]);

    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this product?")) {
            deleteProduct(id, dispatch);
        }
    };

    const handleCategoryFilter = (e) => {
        setFilterCategory((prev) => {
            return { ...prev, category: e ? e.value : undefined };
        });
    };

    const handlePrincipleFilter = (e) => {
        setFilterCategory((prev) => {
            return { ...prev, principle: e ? e.value : undefined };
        });
    };

    const handleResetFilters = () => {
        setFilterCategory({
            status: undefined,
            category: undefined,
            principle: undefined,
            itemCode: undefined,
        });
        setItemCode("");
        setInputs((prev) => ({ ...prev, page: 1 }));
    };

    const totalCount = products?.length ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / LIMIT));
    const paginatedProducts = products
        ? products.slice((inputs.page - 1) * LIMIT, inputs.page * LIMIT)
        : [];

    // Debounced item code filter
    useEffect(() => {
        const timer = setTimeout(() => {
            setFilterCategory((prev) => {
                return { ...prev, itemCode: itemCode || undefined };
            });
        }, 500);

        return () => clearTimeout(timer);
    }, [itemCode]);

    return (
        <div className="container mx-auto px-4 py-8 block">
            {/* Header Section */}
            <div className="flex justify-between items-center mb-6">
                <button
                    onClick={handleResetFilters}
                    className="flex items-center bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
                >
                    <FiPlus className="mr-2" />
                    Reset Filters
                </button>
                {superadmin && (
                    <button
                        onClick={() => {
                            let payload = [];
                            products.forEach((product) => {
                                payload.push({
                                    title: product.title,
                                    category: product.category?.title || 'N/A',
                                    itemCode: product.itemCode || 'N/A',
                                    principle: product.principle?.name || 'N/A',
                                    minimumPrice: product.minimumPrice || 'N/A',
                                });
                            });
                            excel({
                                excelData: payload,
                                fileName: "Products",
                            });
                        }}
                        className="bg-orange-400 text-white px-4 py-2 rounded hover:bg-orange-500"
                    >
                        Export
                    </button>
                )}
            </div>

            {/* Filter Panel */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Item Code
                        </label>
                        <input
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            name="itemCode"
                            value={itemCode}
                            type="text"
                            placeholder="Search by item code"
                            onChange={(e) => setItemCode(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Category
                        </label>
                        <div className="react-select-container">
                            <Select
                                options={categories.map((category) => ({
                                    label: category.title,
                                    value: category._id,
                                }))}
                                onChange={handleCategoryFilter}
                                placeholder="Filter by Category"
                                isClearable
                                {...commonSelectProps}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Principle
                        </label>
                        <div className="react-select-container">
                            <Select
                                options={principles.map((principle) => ({
                                    label: principle.name || principle.email,
                                    value: principle._id,
                                }))}
                                onChange={handlePrincipleFilter}
                                placeholder="Filter by Principle"
                                isClearable
                                {...commonSelectProps}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-orange-400">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                                Item Code
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                                Product Title
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                                Category
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                                Min Price
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                                Principle
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                                Action
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedProducts.length > 0 ? paginatedProducts.map((product) => (
                            <tr key={product._id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {product.itemCode || 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {product.title}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {product.category?.title || 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {product.minimumPrice ? `RM ${product.minimumPrice}` : 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {product.principle?.name || 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <div className="flex items-center space-x-2">
                                        {(superadmin || marketing) && (
                                            <Link
                                                to={"/product/" + product._id}
                                                style={{ textDecoration: "none" }}
                                            >
                                                <button className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs">
                                                    Edit
                                                </button>
                                            </Link>
                                        )}
                                        {superadmin && (
                                            <button
                                                onClick={() => handleDelete(product._id)}
                                                className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs"
                                            >
                                                <DeleteOutline style={{ fontSize: 16 }} />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                                    No products found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {/* Pagination */}
                {totalCount > 0 && (
                    <div className="order-list-table-footer px-4 py-3 flex items-center justify-between border-t border-gray-200">
                        <div className="order-list-table-footer-inner flex-1 flex justify-between items-center">
                            <div className="order-list-table-footer-results text-sm text-gray-700">
                                Showing{" "}
                                <span className="font-medium">
                                    {((inputs.page - 1) * LIMIT) + 1}
                                </span>
                                {" "}-{" "}
                                <span className="font-medium">
                                    {Math.min(inputs.page * LIMIT, totalCount)}
                                </span>
                                {" "}of{" "}
                                <span className="font-medium">{totalCount}</span>
                                {" "}results
                            </div>
                            <div className="order-list-table-footer-pagination flex space-x-2">
                                <button
                                    onClick={() => setInputs((prev) => ({ ...prev, page: 1 }))}
                                    disabled={inputs.page === 1}
                                    title="First"
                                    aria-label="First page"
                                    className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                        inputs.page === 1
                                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                            : "bg-orange-400 text-white hover:bg-orange-500"
                                    }`}
                                >
                                    <span className="order-list-pagination-icon" aria-hidden>&lt;&lt;</span>
                                    <span className="order-list-pagination-text">First</span>
                                </button>
                                <button
                                    onClick={() => setInputs((prev) => ({ ...prev, page: prev.page - 1 }))}
                                    disabled={inputs.page === 1}
                                    title="Previous"
                                    aria-label="Previous page"
                                    className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                        inputs.page === 1
                                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                            : "bg-orange-400 text-white hover:bg-orange-500"
                                    }`}
                                >
                                    <span className="order-list-pagination-icon" aria-hidden>&lt;</span>
                                    <span className="order-list-pagination-text">Previous</span>
                                </button>
                                <span className="order-list-pagination-page px-3 py-1 bg-gray-100 text-gray-900 rounded-md text-sm font-medium">
                                    Page {inputs.page} of {totalPages}
                                </span>
                                <button
                                    onClick={() => setInputs((prev) => ({ ...prev, page: prev.page + 1 }))}
                                    disabled={inputs.page >= totalPages}
                                    title="Next"
                                    aria-label="Next page"
                                    className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                        inputs.page >= totalPages
                                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                            : "bg-orange-400 text-white hover:bg-orange-500"
                                    }`}
                                >
                                    <span className="order-list-pagination-icon" aria-hidden>&gt;</span>
                                    <span className="order-list-pagination-text">Next</span>
                                </button>
                                <button
                                    onClick={() => setInputs((prev) => ({ ...prev, page: totalPages }))}
                                    disabled={inputs.page >= totalPages}
                                    title="Last"
                                    aria-label="Last page"
                                    className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                        inputs.page >= totalPages
                                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                            : "bg-orange-400 text-white hover:bg-orange-500"
                                    }`}
                                >
                                    <span className="order-list-pagination-icon" aria-hidden>&gt;&gt;</span>
                                    <span className="order-list-pagination-text">Last</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
